import { useForm } from "react-hook-form";
import { EditLeaveFormValues, EditLeaveSchema } from "../schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { LeaveRequest } from "src/redux/types/_attendance.type";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "src/components/form/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/input/Select";
import { Input } from "src/components/input/input";
import { Textarea } from "src/components/input/TextArea";
import { Button } from "src/components/buttons/shadcn-button";
import { useEditLeave } from "../hooks/uesEditLeave";

const leaveTypes = [
  { value: "CL", label: "Casual Leave" },
  { value: "SL", label: "Sick Leave" },
  { value: "PLL", label: "Planned Leave" },
  { value: "MTL", label: "Maternity Leave" },
  { value: "PTL", label: "Paternity Leave" },
  { value: "LWP", label: "Leave Without Pay" },
];

const EditLeaveForm = ({leave, close}: {leave: LeaveRequest, close: () => void}) => {

  const form = useForm<Partial<EditLeaveFormValues>>({
    resolver: zodResolver(EditLeaveSchema),
    defaultValues: {
      type: leave.type,
      from_date: new Date(leave.from_date).toISOString().split("T")[0],
      to_date: new Date(leave.to_date).toISOString().split("T")[0],
      days: Number(leave.days),
      reason: leave.reason,
    },
  });

  const { mutate : editLeave, isPending } = useEditLeave();

  const onSubmit = (values: Partial<EditLeaveFormValues>) => {
    editLeave({ id: leave.id, values})
  }

  return (
    <div>
      <Form {...form}>
        <form className="space-y-3" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="md:grid grid-cols-2 gap-4 space-y-3 md:space-y-0">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type of Leave Request</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Leave type" />
                      </SelectTrigger>
                      <SelectContent>
                        {leaveTypes.map((leave) => (
                          <SelectItem key={leave.value} value={leave.value}>
                            {leave.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="from_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leave From Date</FormLabel>

                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="to_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Leave To Date</FormLabel>

                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>

                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number of Days of Leave</FormLabel>
                  <FormControl>
                    <Input {...field} type="number" />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <div className="md:col-span-2">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reason of Leave</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Reason of Leave Request"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="my-6 flex justify-between">
            <Button onClick={close} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              className="bg-primary text-white py-3 rounded-lg font-medium"
              type="submit"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting || isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default EditLeaveForm